import React, { useState, useEffect } from 'react';
import {
  Container, Paper, TextField, Button, Typography,
  Box, Link, Alert, CircularProgress, InputAdornment, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Divider, Checkbox, FormControlLabel
} from '@mui/material';
import {
  Visibility, VisibilityOff, Lock, CheckCircle, Cancel,
  Person, Email, Business
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@config/api';

interface DoctorInfo {
  email: string;
  firstName: string;
  lastName: string;
}

const SetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfo | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const type = searchParams.get('type');

  // Validari parola in timp real
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const isPasswordValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;

  useEffect(() => {
    if (!token) {
      setError('Token-ul de activare lipseste. Te rog sa folosesti link-ul din email.');
      setIsLoadingInfo(false);
      return;
    }

    if (type !== 'doctor') {
      setError('Acest link este valid doar pentru activarea conturilor de doctor.');
      setIsLoadingInfo(false);
      return;
    }

    // Încarcă informațiile doctorului pentru afișare (opțional - doar pentru UX mai bun)
    // Deocamdată nu avem endpoint pentru asta, așa că setăm loading false
    setIsLoadingInfo(false);
  }, [token, type]);

  const handleSubmit = async (event?: React.FormEvent | React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!token) {
      setError('Token-ul de activare lipseste. Te rog sa folosesti link-ul din email.');
      return;
    }

    if (!password) {
      setError('Te rog sa introduci o parola noua');
      return;
    }

    if (!acceptedTerms) {
      setError('Trebuie să accepți termenii și condițiile pentru a continua');
      return;
    }

    if (!isPasswordValid) {
      setError('Parola nu îndeplineste toate cerintele de securitate');
      return;
    }

    if (password !== confirmPassword) {
      setError('Parolele nu se potrivesc');
      return;
    }

    setError('');
    setMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/set-password-doctor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token,
          newPassword: password 
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Salvăm info despre doctor pentru mesaj personalizat
        if (data.data) {
          setDoctorInfo({
            email: data.data.email,
            firstName: data.data.firstName,
            lastName: data.data.lastName
          });
        }
        
        setMessage('Parola a fost setata cu succes si contul tau a fost activat! Te redirectionam la pagina de login...');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Eroare la setarea parolei. Token-ul poate fi expirat sau invalid.');
      }
    } catch (error: any) {
      console.error('Error setting password:', error);
      setError('Eroare de retea. Te rog sa incerci din nou.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const PasswordRequirement = ({ met, text }: { met: boolean; text: string }) => (
    <ListItem dense sx={{ py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {met ? (
          <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
        ) : (
          <Cancel sx={{ fontSize: 18, color: 'text.disabled' }} />
        )}
      </ListItemIcon>
      <ListItemText 
        primary={text}
        primaryTypographyProps={{
          variant: 'body2',
          color: met ? 'text.primary' : 'text.secondary'
        }}
      />
    </ListItem>
  );

  if (isLoadingInfo) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Se incarca...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <form onSubmit={handleSubmit}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Health V2
            </Typography>
            
            <Typography variant="h5" sx={{ mb: 1 }}>
              Bun venit, Doctor! 👋
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Seteaza-ti parola pentru a-ti activa contul.
            </Typography>

            {message && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!error && !message && (
              <>
                <TextField
                  required
                  fullWidth
                  label="Parola noua"
                  type={showPassword ? 'text' : 'password'}
                  margin="normal"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  required
                  fullWidth
                  label="Confirma parola noua"
                  type={showConfirmPassword ? 'text' : 'password'}
                  margin="normal"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  error={confirmPassword.length > 0 && !passwordsMatch}
                  helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Parolele nu se potrivesc' : ''}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleConfirmPasswordVisibility} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Paper variant="outlined" sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Parola trebuie sa contina:
                  </Typography>
                  <List dense disablePadding>
                    <PasswordRequirement met={hasMinLength} text="Minim 8 caractere" />
                    <PasswordRequirement met={hasUpperCase} text="O litera mare (A-Z)" />
                    <PasswordRequirement met={hasLowerCase} text="O litera mica (a-z)" />
                    <PasswordRequirement met={hasNumber} text="Un numar (0-9)" />
                    <PasswordRequirement met={hasSpecialChar} text="Un caracter special (!@#$%^&*)" />
                    {confirmPassword.length > 0 && (
                      <PasswordRequirement met={passwordsMatch} text="Parolele se potrivesc" />
                    )}
                  </List>
                </Paper>

                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mt: 2, 
                    mb: 2, 
                    p: 2, 
                    bgcolor: acceptedTerms ? 'success.50' : 'background.default',
                    borderColor: acceptedTerms ? 'success.main' : 'divider',
                    borderWidth: acceptedTerms ? 2 : 1
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        color="success"
                        disabled={isLoading}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Accept{' '}
                        <Link 
                          href="/terms" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600 }}
                        >
                          termenii și condițiile
                        </Link>
                        {' '}de utilizare și{' '}
                        <Link 
                          href="/privacy" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600 }}
                        >
                          politica de confidențialitate
                        </Link>
                        {' '}*
                      </Typography>
                    }
                  />
                  {!acceptedTerms && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Trebuie să accepți termenii și condițiile pentru a-ți activa contul.
                    </Alert>
                  )}
                </Paper>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 2, mb: 2 }}
                  disabled={isLoading || !token || !isPasswordValid || !passwordsMatch || !acceptedTerms}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Activeaza Contul'}
                </Button>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Ai deja cont activ?
              </Typography>
              <Link component={RouterLink} to="/login" variant="body2">
                Inapoi la Login
              </Link>
            </Box>
          </Paper>
        </form>
      </Box>
    </Container>
  );
};

export default SetPassword;

